import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldCheck, TrendingUp, Award, Check, Gauge, Rocket, RefreshCw, Tag } from "lucide-react";
import { fetchMatchedSystems, rebuildMatches, monthlyPayment, money, fetchDiscounts, saveDiscount } from "@/lib/dashboardApi";

const MANUFACTURERS = ["ACiQ", "Rheem"];
const SYSTEMS = ["Split System", "Mini Split", "Package Unit", "PTAC"];
const MODES = ["Heat Pump", "Straight Cool"];
const TIERS = [
  { key: "good", name: "GOOD", sub: "Essential Comfort", ring: "border-[#16A34A]", head: "bg-[#16A34A]", price: "text-[#15803D]", btn: "bg-[#16A34A] hover:bg-[#15803D]", flag: "Smart Value", icon: ShieldCheck },
  { key: "better", name: "BETTER", sub: "Enhanced Comfort", ring: "border-[#F97316]", head: "bg-[#F97316]", price: "text-[#C2410C]", btn: "bg-[#F97316] hover:bg-[#EA580C]", flag: "Most Popular", icon: TrendingUp },
  { key: "best", name: "BEST", sub: "Ultimate Comfort", ring: "border-[#0284C7]", head: "bg-[#0284C7]", price: "text-[#0369A1]", btn: "bg-[#0284C7] hover:bg-[#0369A1]", flag: "Best Value", icon: Award },
];

export default function SellingComfort() {
  const navigate = useNavigate();
  const [man, setMan] = useState("ACiQ");
  const [system, setSystem] = useState("");
  const [mode, setMode] = useState("");
  const [ton, setTon] = useState("");
  const [rows, setRows] = useState(null);
  const [rebuilding, setRebuilding] = useState(false);
  const [picks, setPicks] = useState({ good: "", better: "", best: "" });
  const [loadCtx, setLoadCtx] = useState(null);
  const [discounts, setDiscounts] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [appliedIds, setAppliedIds] = useState([]);
  const [showEditor, setShowEditor] = useState(false);
  const [edits, setEdits] = useState({});
  const loadDiscounts = () => fetchDiscounts().then((d) => { setDiscounts((d.items || []).filter((x) => x.active)); setCanEdit(!!d.can_edit); }).catch(() => {});
  useEffect(() => { loadDiscounts(); }, []);
  useEffect(() => { setEdits(Object.fromEntries(discounts.map((d) => [d.id, { kind: d.kind || "percent", amount: d.amount == null ? "" : String(d.amount) }]))); }, [discounts]);
  const applied = discounts.filter((d) => appliedIds.includes(d.id) && d.amount != null);
  const discLabel = (d) => (d.kind === "percent" ? `${d.amount}%` : money(d.amount));
  const saveDisc = async (d) => {
    const e = edits[d.id] || {};
    try { await saveDiscount(d.id, { kind: e.kind, amount: e.amount === "" ? null : Number(e.amount) }); toast.success(`${d.name} updated`); loadDiscounts(); }
    catch { toast.error("Could not save discount (owner/admin only)"); }
  };
  const applyDisc = (price) => {
    let total = 0; const lines = [];
    for (const d of applied) {
      const raw = d.kind === "percent" ? price * (d.amount / 100) : d.amount;
      const a = Math.max(0, Math.min(raw, price - total));
      total += a; lines.push({ name: d.name, amount: a });
    }
    return { final: Math.max(0, price - total), total, lines, pct: price ? (total / price) * 100 : 0 };
  };

  useEffect(() => {
    const raw = sessionStorage.getItem("sellingComfortLoad");
    if (raw) { try { const c = JSON.parse(raw); setLoadCtx(c); if (c.tonnage) setTon(String(c.tonnage)); } catch (e) { console.error("Failed to parse sellingComfortLoad:", e); } sessionStorage.removeItem("sellingComfortLoad"); }
  }, []);

  const load = async () => {
    setRows(null);
    const params = { manufacturer: man };
    if (system) params.config = system;
    if (mode) params.mode = mode;
    try { setRows(await fetchMatchedSystems(params)); }
    catch { toast.error("Could not load matched systems"); setRows([]); }
  };
  useEffect(() => { load(); setPicks({ good: "", better: "", best: "" }); /* eslint-disable-next-line */ }, [man, system, mode]);

  const tonnages = useMemo(() => Array.from(new Set((rows || []).map((r) => r.tonnage).filter(Boolean))).sort(), [rows]);
  const filtered = useMemo(() => {
    let list = rows || [];
    if (ton) list = list.filter((r) => r.tonnage === ton);
    return list;
  }, [rows, ton]);

  // auto-suggest good/better/best by price once list loads and nothing chosen
  useEffect(() => {
    if (!filtered.length) return;
    setPicks((p) => {
      if (p.good || p.better || p.best) return p;
      const sorted = [...filtered].sort((a, b) => a.price - b.price);
      return {
        good: sorted[0]?.id || "",
        better: (sorted.length >= 3 ? sorted[Math.floor(sorted.length / 2)] : sorted[1] || sorted[0])?.id || "",
        best: sorted[sorted.length - 1]?.id || "",
      };
    });
  }, [filtered]);

  const byId = (id) => (rows || []).find((r) => r.id === id);
  const rebuild = async () => {
    setRebuilding(true);
    try { const r = await rebuildMatches(); toast.success(`Rebuilt ${r.matched_systems} matched systems`); load(); }
    catch { toast.error("Rebuild failed (owner/admin only)"); }
    finally { setRebuilding(false); }
  };

  const sendToProposal = () => {
    if (!picks.good && !picks.better && !picks.best) { toast.error("Pick at least one system"); return; }
    sessionStorage.setItem("launchSelectPrefill", JSON.stringify({
      mode: "gbb_systems", source: "Selling Comfort", popular_tier: "better",
      tiers_eq: picks,
      discount_ids: appliedIds,
      discounts: applied,
      title: `${man} Comfort Options${loadCtx?.tonnage ? ` · ${loadCtx.tonnage} ton` : ""}`,
      customer_name: loadCtx?.customer_name || "", customer_phone: loadCtx?.customer_phone || "",
      customer_email: loadCtx?.customer_email || "", customer_address: loadCtx?.address || "",
    }));
    toast.success("Sending Good/Better/Best to a new proposal…");
    navigate("/admin/launch-select");
  };

  return (
    <div data-testid="selling-comfort-page">
      <div className="text-center max-w-3xl mx-auto">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-launch">Your Comfort, Three Ways</p>
        <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-navy">We Aren't Selling Equipment.<br className="hidden sm:block" /> We're Selling Comfort.</h1>
        <p className="mt-3 text-slate-600">Every system below is a real, matched {man} system from your catalog, priced all-in. Pick a Good, Better, and Best, then send them straight into a proposal.</p>
      </div>

      {loadCtx?.tonnage && (
        <div className="mt-5 mx-auto max-w-md bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center" data-testid="sc-load-context">
          <p className="text-sm text-emerald-800 flex items-center justify-center gap-2"><Gauge className="w-4 h-4" /> From Load Calc: <b>{loadCtx.tonnage} tons</b> recommended{loadCtx.system_type ? ` · ${loadCtx.system_type}` : ""}. Matching systems are highlighted.</p>
        </div>
      )}

      {/* Controls */}
      <div className="mt-6 flex flex-wrap items-end gap-4 justify-center">
        <div>
          <p className="text-[11px] font-semibold text-slate-500 uppercase mb-1">Manufacturer</p>
          <div className="flex gap-2" data-testid="sc-manufacturer">
            {MANUFACTURERS.map((m) => (
              <button key={m} onClick={() => setMan(m)} data-testid={`sc-man-${m}`}
                className={`px-5 py-2 rounded-full text-sm font-bold border transition-colors ${man === m ? "bg-navy text-white border-navy" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>{m}</button>
            ))}
          </div>
        </div>
        <div>
          <p className="text-[11px] font-semibold text-slate-500 uppercase mb-1">System</p>
          <select value={system} onChange={(e) => setSystem(e.target.value)} data-testid="sc-system" className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
            <option value="">All systems</option>
            {SYSTEMS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <p className="text-[11px] font-semibold text-slate-500 uppercase mb-1">Type</p>
          <select value={mode} onChange={(e) => setMode(e.target.value)} data-testid="sc-mode" className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
            <option value="">All types</option>
            {MODES.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div>
          <p className="text-[11px] font-semibold text-slate-500 uppercase mb-1">Tonnage</p>
          <select value={ton} onChange={(e) => setTon(e.target.value)} data-testid="sc-tonnage" className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
            <option value="">All sizes</option>
            {tonnages.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <button onClick={rebuild} disabled={rebuilding} data-testid="sc-rebuild" className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-navy border border-slate-200 rounded-lg px-3 py-2">
          {rebuilding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Rebuild Prices
        </button>
      </div>

      {/* Tier pickers */}
      {discounts.length > 0 && (
        <div className="mt-6 max-w-3xl mx-auto bg-white border border-slate-200 rounded-xl p-4" data-testid="sc-discounts">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2"><Tag className="h-4 w-4 text-launch" /><p className="text-sm font-bold text-navy">Apply Discounts &amp; Rebates</p></div>
            {canEdit && <button onClick={() => setShowEditor((v) => !v)} data-testid="sc-disc-edit-toggle" className="text-xs font-semibold text-slate-500 hover:text-navy">{showEditor ? "Done" : "Set amounts"}</button>}
          </div>
          <div className="flex flex-wrap gap-2">
            {discounts.map((d) => {
              const configured = d.amount != null;
              const on = appliedIds.includes(d.id);
              return (
                <button key={d.id} disabled={!configured}
                  onClick={() => setAppliedIds((ids) => on ? ids.filter((x) => x !== d.id) : [...ids, d.id])}
                  data-testid={`sc-disc-${d.id}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${!configured ? "bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed" : on ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
                  {d.name} · {configured ? discLabel(d) : "not set"}{d.authorization_required ? " *" : ""}
                </button>
              );
            })}
          </div>
          {applied.some((d) => d.authorization_required) && <p className="mt-2 text-[11px] text-amber-600">* Requires manager authorization.</p>}
          {canEdit && showEditor && (
            <div className="mt-3 pt-3 border-t border-slate-100 space-y-2" data-testid="sc-disc-editor">
              {discounts.map((d) => {
                const e = edits[d.id] || { kind: "percent", amount: "" };
                return (
                  <div key={d.id} className="flex items-center gap-2 text-xs">
                    <span className="flex-1 text-slate-700 font-medium truncate">{d.name}</span>
                    <select value={e.kind} onChange={(ev) => setEdits((s) => ({ ...s, [d.id]: { ...e, kind: ev.target.value } }))}
                      data-testid={`sc-disc-kind-${d.id}`} className="border border-slate-200 rounded px-2 py-1">
                      <option value="percent">%</option>
                      <option value="flat">$</option>
                    </select>
                    <input value={e.amount} onChange={(ev) => setEdits((s) => ({ ...s, [d.id]: { ...e, amount: ev.target.value } }))}
                      placeholder="amount" inputMode="decimal" data-testid={`sc-disc-amount-${d.id}`}
                      className="w-24 border border-slate-200 rounded px-2 py-1" />
                    <button onClick={() => saveDisc(d)} data-testid={`sc-disc-save-${d.id}`} className="px-2.5 py-1 rounded bg-navy text-white font-semibold">Save</button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      {rows === null ? (
        <div className="flex items-center justify-center py-24 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : (
        <>
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="sc-tiers">
            {TIERS.map((t) => {
              const sel = byId(picks[t.key]);
              const dd = sel ? applyDisc(sel.price) : null;
              const Icon = t.icon;
              return (
                <div key={t.key} className={`relative flex flex-col rounded-2xl bg-white border-t-4 ${t.ring} border-x border-b border-slate-200 shadow-sm`} data-testid={`sc-card-${t.key}`}>
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide border rounded-full px-3 py-1 bg-white text-slate-600"><Icon className="h-3 w-3" /> {t.flag}</span>
                  </div>
                  <div className="p-5 sm:p-6 flex flex-col h-full">
                    <div className="text-center mt-2">
                      <span className={`inline-block text-white text-xs font-extrabold uppercase tracking-[0.15em] px-4 py-1.5 rounded-full ${t.head}`}>{t.name}</span>
                      <p className="mt-1 text-sm font-semibold text-slate-500">{t.sub}</p>
                    </div>
                    <label className="mt-4 block">
                      <span className="text-[11px] font-semibold text-slate-500">Choose {man} system</span>
                      <select value={picks[t.key]} onChange={(e) => setPicks((p) => ({ ...p, [t.key]: e.target.value }))} data-testid={`sc-select-${t.key}`}
                        className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
                        <option value="">— none —</option>
                        {filtered.map((r) => (
                          <option key={r.id} value={r.id}>{r.model} · {r.tonnage || r.system_type} · {money(r.price)}</option>
                        ))}
                      </select>
                    </label>
                    {sel ? (
                      <div className="mt-4 flex-1">
                        <p className="text-sm font-bold text-navy">{sel.model}</p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          <span className="text-xs font-bold bg-slate-100 text-slate-700 rounded-full px-3 py-1">{sel.system_type}</span>
                          {sel.tonnage ? <span className="text-xs font-bold bg-slate-100 text-slate-700 rounded-full px-3 py-1">{sel.tonnage}</span> : null}
                          {loadCtx?.tonnage && sel.tonnage === `${loadCtx.tonnage} Ton` && <span className="text-xs font-bold bg-emerald-100 text-emerald-700 rounded-full px-3 py-1 flex items-center gap-1"><Check className="w-3 h-3" /> Load match</span>}
                        </div>
                      </div>
                    ) : <div className="mt-4 flex-1 text-sm text-slate-400">Select a system to preview.</div>}
                    <div className="mt-5 pt-4 border-t border-slate-100 text-center">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Aquila Installed Price</p>
                      {sel && dd && dd.total > 0 && <p className="text-sm text-slate-400 line-through" data-testid={`sc-orig-${t.key}`}>{money(sel.price)}</p>}
                      <p className={`mt-1 text-3xl font-black tracking-tight ${t.price}`} data-testid={`sc-price-${t.key}`}>{sel && dd ? money(dd.final) : "—"}</p>
                      {sel && dd && dd.lines.length > 0 && (
                        <div className="mt-2 text-left inline-block" data-testid={`sc-savings-${t.key}`}>
                          {dd.lines.map((l, i) => <p key={i} className="text-[11px] text-emerald-700 flex justify-between gap-4"><span>{l.name}</span>{" "}<span>−{money(l.amount)}</span></p>)}
                        </div>
                      )}
                      {sel && dd && dd.total > 0 && <p className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-0.5" data-testid={`sc-repdisc-${t.key}`} title="Visible to sales rep only — do not show the customer">−{dd.pct.toFixed(1)}% · rep only</p>}
                      {sel && <p className="mt-1 block text-xs font-semibold text-slate-500">as low as {money(monthlyPayment(dd ? dd.final : sel.price, 120))}/mo <span className="text-slate-400 font-normal">· w.a.c.</span></p>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {filtered.length === 0 && <p className="text-center text-slate-400 py-6">No matched {man} systems for this filter. Try a different type or tonnage, or Rebuild Prices.</p>}

          <div className="mt-8 flex justify-center">
            <button onClick={sendToProposal} data-testid="sc-send-proposal"
              className="inline-flex items-center gap-2 bg-launch hover:bg-launch-dark text-white font-bold px-8 py-3 rounded-xl text-sm">
              <Rocket className="w-5 h-5" /> Send Good/Better/Best to Proposal
            </button>
          </div>
          <p className="mt-3 text-center text-xs text-slate-400">Final equipment selection and verification remains with the Comfort Consultant.</p>
        </>
      )}
    </div>
  );
}
